<template>
  <div id="bulk-uploads" class="fill-height align-start">
    <v-row class="g-container" no-gutters>
      <v-col>
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <inline-alert
          v-if="!!alertText"
          class="py-4 py-md-6 px-md-8"
          :messages="alertDetails"
          @close="clearUploadAlerts"
        >
          {{ alertText }}
        </inline-alert>

        <inline-alert
          v-if="isImportMode && isUploadSuccess"
          color="primary"
          class="mt-4 py-2 py-md-4 px-md-8"
          @close="clearUploadAlerts"
        >
          <div>
            <strong>Congratulations</strong> your Bulk upload has been
            successful. Next steps to finalise product setup
            <ul>
              <li>Add Images & Ranging details</li>
              <li>
                Confirm a Publish Date for products to Go Live on the
                {{ isILR ? 'Customer' : 'Retailer' }}
                Marketplace
              </li>
            </ul>
            Bulk uploaded products are now located in
            <a
              class="p-message-link"
              @click="
                $router.push({ name: 'products', query: { status: 'DRAFT' } })
              "
              ><b>Draft</b></a
            >
            tab in
            <a
              class="p-message-link g-bold"
              @click="
                $router.push({ name: 'products', query: { status: 'DRAFT' } })
              "
              >Range & Pricing Details</a
            >
            in
            <a
              class="p-message-link g-bold"
              @click="$router.push({ name: 'data-maintenance' })"
              >Data Setup & Maintenance</a
            ><br />
          </div>
          <v-btn
            rounded
            class="mt-4 mb-1 i-btn-block-secondary"
            :to="{ name: 'products', query: { status: 'DRAFT' } }"
            >Access Draft tab</v-btn
          >
        </inline-alert>

        <inline-alert
          v-if="isPriceUpdateMode && isPriceUploadSuccess"
          color="primary"
          class="mt-4 py-2 py-md-4 px-md-8"
          @close="clearUploadAlerts"
        >
          <div>
            <strong>Price changes uploaded successfully.</strong>
            Products with changed prices have been submitted through the
            approval workflow and can be reviewed in
            <a
              class="p-message-link g-bold"
              @click="
                $router.push({
                  name: 'products',
                  query: { status: 'AWAITING_APPROVAL' }
                })
              "
              >Awaiting Approval</a
            >.
          </div>
        </inline-alert>

        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="4" md="3" lg="2">
            <i-instructions
              instructionKey="data-maintenance/products/bulk-upload"
            ></i-instructions>
          </v-col>
        </v-row>

        <v-row v-if="isImportMode" class="ma-0" no-gutters>
          <v-col cols="12" md="6" class="d-flex pr-md-4 my-2 my-md-4">
            <v-card class="flex-grow-1 py-4 px-6">
              <p class="g-bold g-heading-s">Download</p>

              <v-divider class="my-4"></v-divider>
              <v-row>
                <v-col cols="12" sm="6" class="mb-4 mb-sm-0 py-0">
                  <v-card
                    class="p-action-card pt-lg-6"
                    @click="downloadGroceryFile()"
                  >
                    <div class="p-text-overlay mb-2">
                      <v-icon v-if="!downloadingGroceryFile" class="p-icon down"
                        >mdi-cloud-download-outline</v-icon
                      >
                      <v-progress-circular
                        v-else
                        indeterminate
                        color="secondary"
                        class="mb-2"
                      ></v-progress-circular>
                      <div>
                        <v-chip class="mb-2" color="primary" small>
                          <span class="p-chip-text">GROCERY</span>
                        </v-chip>
                      </div>
                      Download Product & Pricing file
                    </div>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" class="py-0">
                  <v-card
                    class="p-action-card pt-lg-6"
                    @click="downloadLiquorFile()"
                  >
                    <div class="p-text-overlay mb-2">
                      <v-icon
                        v-if="!downloadingLiquorFile"
                        class="p-icon pb-2 down"
                        >mdi-cloud-download-outline</v-icon
                      >
                      <v-progress-circular
                        v-else
                        indeterminate
                        color="secondary"
                        class="mb-2"
                      ></v-progress-circular>
                      <div>
                        <v-chip class="mb-2" color="primary" small>
                          <span class="p-chip-text">LIQUOR</span>
                        </v-chip>
                      </div>
                      Download Product & Pricing file
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <div>
                <span>Download blank file to provide:</span><br />
                <ul>
                  <li>Product Details</li>
                  <li>Product Pricing</li>
                  <li>Additional Product Information</li>
                </ul>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="6" class="d-flex pl-md-4 my-2 my-md-4">
            <v-card class="flex-grow-1 py-4 px-6">
              <p class="g-bold g-heading-s">Upload</p>

              <v-divider class="my-4"></v-divider>

              <input
                id="template-upload"
                ref="fileUploader"
                type="file"
                accept=".xlsx"
                @change="addTemplate"
                style="display: none"
              />
              <v-card class="p-action-card my-4 pt-lg-6">
                <label for="template-upload">
                  <v-row align="center" no-gutters>
                    <v-col>
                      <template v-if="newTemplate">
                        <p class="p-text-overlay">
                          <v-btn
                            large
                            rounded
                            class="i-btn-block-secondary p-image-button my-2"
                            :loading="uploadingFile"
                            @click="confirmTemplate()"
                            >Confirm</v-btn
                          >
                          <br />
                          <v-btn
                            v-if="!uploadingFile"
                            large
                            rounded
                            class="i-btn-block-red p-image-button my-2"
                            @click="cancelTemplate($event)"
                            >Cancel</v-btn
                          >
                        </p>
                      </template>
                      <template v-else>
                        <div class="p-text-overlay mb-2">
                          <v-icon class="p-icon pb-2 up"
                            >mdi-cloud-upload-outline</v-icon
                          >
                          <div>
                            <v-chip class="mb-2" color="primary" small>
                              <span class="p-chip-text">GROCERY & LIQUOR</span>
                            </v-chip>
                          </div>
                          Click here to upload completed Product & Pricing file
                        </div>
                      </template>
                    </v-col>
                  </v-row>
                </label>
              </v-card>

              <v-divider class="my-4"></v-divider>

              <div>
                <span>Upload completed file with:</span>
                <br />
                <ul>
                  <li>Product Details</li>
                  <li>Product Pricing</li>
                  <li>Additional Product Information</li>
                </ul>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-if="isPriceUpdateMode" class="ma-0" no-gutters>
          <v-col cols="12" md="6" class="d-flex pr-md-4 my-2 my-md-4">
            <v-card class="flex-grow-1 py-4 px-6">
              <p class="g-bold g-heading-s">Download Price Change</p>

              <v-divider class="my-4"></v-divider>
              <v-card
                class="p-action-card my-4 pt-lg-6"
                @click="downloadPriceChangeFile()"
              >
                <div class="p-text-overlay mb-2">
                  <v-icon v-if="!downloadingPriceChangeFile" class="p-icon down"
                    >mdi-cloud-download-outline</v-icon
                  >
                  <v-progress-circular
                    v-else
                    indeterminate
                    color="secondary"
                    class="mb-2"
                  ></v-progress-circular>
                  <div>
                    <v-chip class="mb-2" color="primary" small>
                      <span class="p-chip-text">PRICE CHANGE</span>
                    </v-chip>
                  </div>
                  Download current prices with editable new price columns
                </div>
              </v-card>
              <p class="p-price-change-note">
                Note: Prices can only be updated on products that do not have a
                current or upcoming promotions (excluding Buy Get Free). Only
                products without promotions will be displayed in the price
                update download. End promotions on products to update pricing.
              </p>

              <v-divider class="my-4"></v-divider>

              <div>
                <span>Download file to update:</span><br />
                <ul>
                  <li>Effective date</li>
                  <li>New carton cost / list price</li>
                  <li>New unit RRP</li>
                </ul>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="6" class="d-flex pl-md-4 my-2 my-md-4">
            <v-card class="flex-grow-1 py-4 px-6">
              <p class="g-bold g-heading-s">Upload Price Change</p>

              <v-divider class="my-4"></v-divider>

              <input
                id="price-change-upload"
                ref="priceChangeUploader"
                type="file"
                accept=".xlsx"
                @change="addPriceChangeTemplate"
                style="display: none"
              />
              <v-card class="p-action-card my-4 pt-lg-6">
                <label for="price-change-upload">
                  <v-row align="center" no-gutters>
                    <v-col>
                      <template v-if="newPriceChangeTemplate">
                        <p class="p-text-overlay">
                          <v-btn
                            large
                            rounded
                            class="i-btn-block-secondary p-image-button my-2"
                            :loading="uploadingPriceChangeFile"
                            @click="confirmPriceChangeTemplate()"
                            >Confirm</v-btn
                          >
                          <br />
                          <v-btn
                            v-if="!uploadingPriceChangeFile"
                            large
                            rounded
                            class="i-btn-block-red p-image-button my-2"
                            @click="cancelPriceChangeTemplate($event)"
                            >Cancel</v-btn
                          >
                        </p>
                      </template>
                      <template v-else>
                        <div class="p-text-overlay mb-2">
                          <v-icon class="p-icon pb-2 up"
                            >mdi-cloud-upload-outline</v-icon
                          >
                          <div>
                            <v-chip class="mb-2" color="primary" small>
                              <span class="p-chip-text">PRICE CHANGE</span>
                            </v-chip>
                          </div>
                          Click here to upload completed Price Change file
                        </div>
                      </template>
                    </v-col>
                  </v-row>
                </label>
              </v-card>

              <v-divider class="my-4"></v-divider>

              <div>
                <span>Upload completed file with:</span>
                <br />
                <ul>
                  <li>One effective date</li>
                  <li>Changed new price or RRP values</li>
                  <li>Family group rows edited once</li>
                </ul>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import {
  fetchBulkImportExcel,
  uploadBulkImportExcel,
  uploadPriceChangeExcel
} from '@/services/products'
import { downloadPriceChangeExcel } from '@/services/pricebook'
import InlineAlert from '@/components/InlineAlert'
import { openFile } from '@/utils/common'
import iInstructions from '@/components/Instructions'

const PRODUCT_TYPE = {
  GROCERY: { text: 'Grocery', value: 'GROCERY' },
  LIQUOR: { text: 'Liquor', value: 'LIQUOR' }
}

export default {
  props: {
    mode: {
      type: String,
      default: 'import',
      validator: (value) => ['import', 'price-update'].includes(value)
    }
  },

  components: {
    iInstructions,
    InlineAlert
  },

  data: () => ({
    alertText: undefined,
    alertDetails: [],
    downloadingGroceryFile: false,
    downloadingLiquorFile: false,
    downloadingPriceChangeFile: false,
    isUploadSuccess: false,
    isPriceUploadSuccess: false,
    newTemplate: false,
    newPriceChangeTemplate: false,
    templateUrl: '',
    templateFile: undefined,
    priceChangeTemplateFile: undefined,
    uploadingFile: false,
    uploadingPriceChangeFile: false,
    PRODUCT_TYPE
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('profile', ['info']),
    ...mapGetters('tenant', ['isILR']),

    isImportMode() {
      return this.mode === 'import'
    },

    isPriceUpdateMode() {
      return this.mode === 'price-update'
    },

    productTypeList() {
      return Object.values(PRODUCT_TYPE)
    },

    priceChangeFilename() {
      const supplierName =
        this.info?.name || `supplier-${this.selectedSupplierId}`
      const normalizedSupplierName = supplierName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      return `${normalizedSupplierName || 'supplier'}-bulk upload price change.xlsx`
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    downloadGroceryFile() {
      this.downloadingGroceryFile = true
      this.downloadTemplate(PRODUCT_TYPE.GROCERY.value)
    },

    downloadLiquorFile() {
      this.downloadingLiquorFile = true
      this.downloadTemplate(PRODUCT_TYPE.LIQUOR.value)
    },

    downloadTemplate(productType = PRODUCT_TYPE.GROCERY.value) {
      const selectedType = productType
      fetchBulkImportExcel(selectedType)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          this.addNotification({ color: 'error', text: err })
        })
        .finally(() => {
          this.downloadingGroceryFile = false
          this.downloadingLiquorFile = false
        })
    },

    addTemplate(event) {
      if (!event?.target?.files || event.target.files.length < 1) return
      this.templateFile = event.target.files[0]
      this.templateUrl = URL.createObjectURL(event.target.files[0])
      this.newTemplate = true
      event.target.value = null
    },

    confirmTemplate() {
      this.uploadingFile = true
      uploadBulkImportExcel(this.selectedSupplierId, this.templateFile)
        .then(() => {
          this.isUploadSuccess = true
          this.isPriceUploadSuccess = false
          this.alertText = undefined
          this.alertDetails = []
        })
        .catch((err) => {
          this.isUploadSuccess = false
          switch (err.response?.data?.code || 'UNKNOWN ERROR') {
            case 'EXCEL_PRODUCT_UPLOAD_ERROR':
              this.alertText =
                'Your bulk product upload spreadsheet contains errors and has not been loaded. Fix below errors and Upload again.'
              this.alertDetails = err.response?.data?.fields
              break

            default:
              this.alertText =
                'Your bulk product upload spreadsheet could not be uploaded, please try again...'
          }
        })
        .finally(() => {
          this.cancelTemplate()
          this.uploadingFile = false
        })
    },

    cancelTemplate(event) {
      if (event) event.preventDefault()
      this.templateFile = undefined
      this.templateUrl = ''
      this.newTemplate = false
    },

    downloadPriceChangeFile() {
      this.downloadingPriceChangeFile = true
      downloadPriceChangeExcel()
        .then((file) => {
          this.openBlob(file, this.priceChangeFilename)
        })
        .catch((err) => {
          this.addNotification({ color: 'error', text: err })
        })
        .finally(() => {
          this.downloadingPriceChangeFile = false
        })
    },

    addPriceChangeTemplate(event) {
      if (!event?.target?.files || event.target.files.length < 1) return
      this.priceChangeTemplateFile = event.target.files[0]
      this.newPriceChangeTemplate = true
      event.target.value = null
    },

    confirmPriceChangeTemplate() {
      this.uploadingPriceChangeFile = true
      uploadPriceChangeExcel(this.priceChangeTemplateFile)
        .then(() => {
          this.isPriceUploadSuccess = true
          this.isUploadSuccess = false
          this.alertText = undefined
          this.alertDetails = []
        })
        .catch((err) => {
          this.isPriceUploadSuccess = false
          switch (err.response?.data?.code || 'UNKNOWN ERROR') {
            case 'EXCEL_PRODUCT_UPLOAD_ERROR':
              this.alertText =
                'Your bulk price change spreadsheet contains errors and has not been loaded. Fix below errors and Upload again.'
              this.alertDetails = err.response?.data?.fields
              break

            default:
              this.alertText =
                'Your bulk price change spreadsheet could not be uploaded, please try again...'
          }
        })
        .finally(() => {
          this.cancelPriceChangeTemplate()
          this.uploadingPriceChangeFile = false
        })
    },

    cancelPriceChangeTemplate(event) {
      if (event) event.preventDefault()
      this.priceChangeTemplateFile = undefined
      this.newPriceChangeTemplate = false
    },

    clearUploadAlerts() {
      this.alertText = undefined
      this.alertDetails = []
      this.isUploadSuccess = false
      this.isPriceUploadSuccess = false
    },

    openBlob(file, filename) {
      const fileUrl = window.URL.createObjectURL(file)
      const link = document.createElement('a')
      link.href = fileUrl
      link.download = filename
      link.click()
      window.URL.revokeObjectURL(fileUrl)
    }
  }
}
</script>

<style lang="scss" scoped>
#bulk-uploads {
  .p-action-card {
    background-color: #f8f9fa;
    border-radius: 10px;
    border: 2px dashed $secondary-color;
    padding: 16px 24px;
    box-shadow: unset;
  }
  .p-action-card:hover {
    * {
      cursor: pointer;
    }
    background-color: var(--v-lightHighlightBackground-lighten1);
    transition: background-color 0.3s ease-in-out;
    .down {
      padding-top: 8px;
    }
    .up {
      margin-top: -4px;
      margin-bottom: 4px;
    }
  }
  .p-chip-text {
    font-weight: bold;
    letter-spacing: 1.5px;
    font-size: $text-small-font-size-sm;
  }
  .p-icon {
    color: $secondary-color;
    height: 58px;
    width: 58px;
  }
  .p-icon:before {
    font-size: 58px;
  }
  .p-image-button {
    width: 200px;
  }
  .p-message-link {
    text-decoration: underline;
    color: currentColor;
  }
  .p-price-change-note {
    color: $red-color;
    font-size: 13px;
    font-weight: bold;
    line-height: 1.4;
  }
  .p-text-overlay {
    font-weight: normal;
    text-align: center;
    font-size: 14px;
  }
}

@media only screen and (min-width: 960px) {
  #bulk-uploads {
    .p-action-card {
      min-height: 150px;
    }
  }
}

@media only screen and (min-width: 1260px) {
  #bulk-uploads {
    .p-action-card {
      min-height: 170px;
    }
  }
}
</style>
